import { useAppSelector } from "../state/store";
import {
  resolveEffectiveDomain,
  getChannelIdFromFlowMeta,
} from "../services/helpers/domainScoping";

/**
 * Single source for the flow-scoping values that trigger dropdowns (concern A)
 * and product/domain fetchers (concern B) all derive identically from state.
 *
 * Before this hook every call site re-pulled the same three selectors
 * (domainChannelMap, flowMeta, flowId) and re-ran the same derivation. Callers
 * now read what they need off one object; a site needing a variant (e.g. a
 * different channelSettingsId rule) computes that one line from `flowMetaEntry`.
 */
export const useFlowScope = () => {
  const flowId = useAppSelector((state) => state.homeState.flowId);
  const flowMeta: { [key: string]: any } = useAppSelector(
    (state) => state.flowMetaState.flowMeta
  );
  const domainChannelMap = useAppSelector(
    (state) => state.flowMetaState.domainChannelMap
  );

  const flowMetaEntry = flowMeta[flowId as string];
  // Concern A: the domain the flow is pinned to (raw, for dropdown filtering).
  const domain: string | undefined = flowMetaEntry?.domain;
  // The channel the flow's triggers use — needed to resolve the product domain.
  const triggerChannelId = getChannelIdFromFlowMeta(flowMetaEntry, domainChannelMap);
  // Concern B: the domain product/collection/discount fetchers must hit
  // (parent store → undefined so the API targets the parent doc).
  const productDomain = resolveEffectiveDomain(
    domain,
    triggerChannelId,
    domainChannelMap
  );
  const channelSettingsId =
    Object.keys(flowMetaEntry?.channelSettings ?? {})[0] ?? "";

  return {
    flowId,
    flowMetaEntry,
    domainChannelMap,
    domain,
    productDomain,
    triggerChannelId,
    channelSettingsId,
  };
};
